import { get } from "svelte/store";
import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
import { GET_STOCK_SITE_LOCATIONS } from "@modern/queries/stock_sites/index";
import { query } from "svelte-apollo";
import { EventTracking } from "@shared/services/event_tracking/events";
import { RetailerUserAuthenticationEventEntity } from "@shared/services/event_tracking/entities/retailer_user_authentication_event_entity";
import { stagedRolloutCheck } from "@shared/services/staged_rollouts";
import * as Sentry from "@sentry/browser";

export function loginEvent(status, distributorId, attrs = {}) {
  let entity = new RetailerUserAuthenticationEventEntity({
    ...attrs,
    distributorId: distributorId,
    authenticationStatus: status,
  });
  EventTracking.trackClick("retailer_user_authentication_login_button", [entity]);
}

export function createAccountEvent(status, distributorId, attrs = {}) {
  let entity = new RetailerUserAuthenticationEventEntity({
    ...attrs,
    distributorId: distributorId,
    authenticationStatus: status,
  });
  EventTracking.trackClick("retailer_user_authentication_create_account_button", [entity]);
}

export function shouldHideAllDistributorInfo(
  retailerUserAuthentications,
  distributorIds = [],
): boolean {
  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  if (!hasRetailerUserAuthRollout) {
    return false;
  }

  if (distributorIds.length === 0 || distributorIds[0] === null) {
    return false;
  }

  let distributorRequiresAuth: boolean;
  let hasRetailerUserAuthentication: boolean;

  distributorRequiresAuth = get(DistributorsStore).some(d => {
    return distributorIds.includes(d.id) && d.require_retailer_user_authentication;
  });

  hasRetailerUserAuthentication = distributorIds.every(distributorId => {
    return retailerUserAuthentications.some(
      auth => auth.distributor_id == distributorId && auth.status == "valid",
    );
  });

  return distributorRequiresAuth && !hasRetailerUserAuthentication;
}

export function getRetailerUserAuthenticationStatus(
  retailerUserAuthentications,
  distributorIds = [],
) {
  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  if (!hasRetailerUserAuthRollout) {
    return;
  }

  let distributorRequiresAuth: boolean;
  let hasRetailerUserAuthentication: boolean;
  let hasInvalidRetailerUserAuthentication: boolean;

  distributorIds = distributorIds.length ? distributorIds : get(DistributorsStore).map(d => d.id);

  distributorRequiresAuth = get(DistributorsStore).some(d => {
    return distributorIds.includes(d.id) && d.require_retailer_user_authentication;
  });

  hasRetailerUserAuthentication = distributorIds.some(d => {
    return retailerUserAuthentications.some(
      auth => auth.distributor_id == d && auth.status == "valid",
    );
  });

  hasInvalidRetailerUserAuthentication = distributorIds.some(d => {
    return retailerUserAuthentications.some(
      auth => auth.distributor_id == d && auth.status == "invalid",
    );
  });

  if (!distributorRequiresAuth) {
    return null;
  } else if (hasRetailerUserAuthentication) {
    return "valid";
  } else if (hasInvalidRetailerUserAuthentication) {
    return "invalid";
  } else {
    return "pending";
  }
}

type StockSite = {
  address: string;
  city: string;
  region: string;
  postalCode: string;
  name: string;
  storeWebsite: string;
  storePhone: string;
  storeEmail: string;
};

type StockSiteLocationsQueryResult = {
  getStockSites: {
    nodes: StockSite[];
  };
};

export async function getStockSiteLocation(distributorId, customerId) {
  let variables = {
    distributorId: String(distributorId),
    stockSiteInput: {
      eligibility: {
        customerId: String(customerId),
      },
    },
  };
  try {
    const res = query<StockSiteLocationsQueryResult>(GET_STOCK_SITE_LOCATIONS, { variables });
    const result = await res?.result();
    return result?.data?.getStockSites?.nodes?.[0] || ({} as StockSite);
  } catch (error) {
    Sentry.captureException(error);
    return {} as StockSite;
  }
}
